import type {
  MoveAction,
  RunHorsesPosition,
  RunHorsesState,
} from "@cloudnest/run-horses-engine";
import {
  isNoKnightLandCell,
  isOasis,
  RUN_HORSES_BOARD_SIZE,
} from "@cloudnest/run-horses-engine";

interface RunHorsesBoardViewProps {
  readonly state: RunHorsesState;
  readonly lastAction?: MoveAction | null;
}

const SIZE = RUN_HORSES_BOARD_SIZE;

function samePos(
  a: RunHorsesPosition | undefined,
  row: number,
  col: number,
): boolean {
  return a !== undefined && a.row === row && a.col === col;
}

/**
 * Read-only 11×11 board renderer for 말달리자. Highlights the oasis, the
 * no-knight diamond, both players' horses, and the last move. Pure presentation
 * — drives off engine state only, no interactivity (used by replay mode).
 */
export function RunHorsesBoardView({
  state,
  lastAction,
}: RunHorsesBoardViewProps) {
  const cells: React.ReactNode[] = [];
  for (let row = 0; row < SIZE; row += 1) {
    for (let col = 0; col < SIZE; col += 1) {
      const occupant = state.board[row]?.[col] ?? "empty";
      const oasis = isOasis(row, col);
      const noKnight = !oasis && isNoKnightLandCell(row, col);
      const isFrom = samePos(lastAction?.from, row, col);
      const isTo = samePos(lastAction?.to, row, col);

      const background = oasis
        ? "var(--cn-run-horses-oasis)"
        : noKnight
          ? "var(--cn-run-horses-no-knight)"
          : (row + col) % 2 === 0
            ? "var(--cn-run-horses-cell)"
            : "var(--cn-run-horses-cell-alt)";

      cells.push(
        <div
          key={`${row}-${col}`}
          data-testid={`run-horses-cell-${row}-${col}`}
          data-occupant={occupant}
          data-oasis={oasis ? "true" : undefined}
          className="relative flex items-center justify-center"
          style={{
            background,
            boxShadow: isTo
              ? "inset 0 0 0 3px var(--cn-run-horses-last-to)"
              : isFrom
                ? "inset 0 0 0 3px var(--cn-run-horses-last-from)"
                : "inset 0 0 0 0.5px var(--cn-run-horses-grid-line)",
            outline: oasis
              ? "2px solid var(--cn-run-horses-oasis-ring)"
              : undefined,
            outlineOffset: oasis ? "-2px" : undefined,
          }}
        >
          {occupant !== "empty" && (
            <span
              role="img"
              aria-label={occupant === "p1" ? "흑 말" : "백 말"}
              className="flex items-center justify-center rounded-full font-bold"
              style={{
                width: "82%",
                height: "82%",
                fontSize: "min(3.2vw, 18px)",
                color:
                  occupant === "p1"
                    ? "var(--cn-run-horses-white)"
                    : "var(--cn-run-horses-black)",
                background:
                  occupant === "p1"
                    ? "var(--cn-run-horses-black)"
                    : "var(--cn-run-horses-white)",
                boxShadow: `0 0 0 2px ${
                  occupant === "p1"
                    ? "var(--cn-run-horses-black-ring)"
                    : "var(--cn-run-horses-white-ring)"
                }`,
              }}
            >
              {occupant === "p1" ? "♞" : "♘"}
            </span>
          )}
        </div>,
      );
    }
  }

  return (
    <div
      data-testid="run-horses-board"
      className="grid w-full select-none rounded-md"
      style={{
        aspectRatio: "1 / 1",
        gridTemplateColumns: `repeat(${SIZE}, 1fr)`,
        gridTemplateRows: `repeat(${SIZE}, 1fr)`,
        background: "var(--cn-run-horses-board-bg)",
        padding: "2px",
        gap: "1px",
        maxWidth: "min(92vw, 560px)",
      }}
    >
      {cells}
    </div>
  );
}
